import { useState } from 'react';
import { 
  ChevronDown, ChevronUp, AlertTriangle, Shield, Target, Clock, 
  MapPin, FileText, Download, Eye, CheckCircle, XCircle, AlertCircle,
  Globe, Plane, Ship, Crosshair, Activity, TrendingUp, BarChart3,
  Lock, Unlock, Info, ExternalLink, Layers, Radar, Zap, Flag
} from 'lucide-react';

// ==========================================
// PROFESSIONAL INTELLIGENCE REPORT UI SYSTEM
// Based on NATO/OSINT reporting standards
// ==========================================

// Classification Banner Component
export function ClassificationBanner({ level = 'UNCLASSIFIED', tlp = 'CLEAR' }) {
  const classColors = {
    'TOP SECRET': 'bg-amber-500 text-black border-amber-600',
    'SECRET': 'bg-red-600 text-white border-red-700',
    'CONFIDENTIAL': 'bg-blue-600 text-white border-blue-700',
    'RESTRICTED': 'bg-green-600 text-white border-green-700',
    'UNCLASSIFIED': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
  };
  
  const tlpColors = {
    'RED': 'bg-red-500/20 text-red-400',
    'AMBER': 'bg-amber-500/20 text-amber-400', 
    'GREEN': 'bg-green-500/20 text-green-400',
    'CLEAR': 'bg-zinc-500/20 text-zinc-400'
  };

  return (
    <div className={`p-3 text-center font-bold border-2 ${classColors[level] || classColors['UNCLASSIFIED']} relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-pulse"></div>
      </div>
      <div className="relative flex items-center justify-center gap-4">
        <Lock className="w-4 h-4" />
        <span className="tracking-[0.3em] text-sm">{level}</span>
        {tlp && (
          <span className={`px-2 py-0.5 text-[10px] rounded ${tlpColors[tlp] || tlpColors['CLEAR']}`}>
            TLP:{tlp}
          </span>
        )}
        <Lock className="w-4 h-4" />
      </div>
    </div>
  );
}

// Report Header with Metadata
export function ReportHeader({ 
  title, 
  subtitle,
  reportId,
  date,
  analyst = 'DEFENSEWATCH AI',
  distribution = 'AUTHORIZED PERSONNEL',
  threatLevel,
  confidence
}) {
  const getThreatStyle = (threat) => {
    const styles = {
      'CRITICAL': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50', icon: AlertTriangle },
      'HIGH': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/50', icon: AlertTriangle },
      'MODERATE': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50', icon: AlertCircle },
      'LOW': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/50', icon: CheckCircle },
      'MINIMAL': { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/50', icon: Shield }
    };
    return styles[threat] || styles['MODERATE'];
  };

  const threatStyle = threatLevel ? getThreatStyle(threatLevel) : null;
  const ThreatIcon = threatStyle?.icon || Shield;

  return (
    <div className="relative overflow-hidden">
      {/* Decorative top line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#00FF9D] to-transparent"></div>
      
      <div className="p-6 bg-gradient-to-br from-black/60 to-zinc-900/40 border-b border-white/10">
        {/* Report Title */}
        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-zinc-400 mt-1 font-mono">{subtitle}</p>
          )}
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="p-3 bg-black/40 border border-white/10 rounded">
            <div className="text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Report ID
            </div>
            <div className="font-mono text-[#00FF9D]">{reportId?.slice(0, 8) || 'PENDING'}</div>
          </div>
          
          <div className="p-3 bg-black/40 border border-white/10 rounded">
            <div className="text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Generated
            </div>
            <div className="font-mono text-zinc-300">{date || new Date().toISOString().split('T')[0]}</div>
          </div>
          
          <div className="p-3 bg-black/40 border border-white/10 rounded">
            <div className="text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Target className="w-3 h-3" /> Analyst
            </div>
            <div className="font-mono text-cyan-400">{analyst}</div>
          </div>
          
          <div className="p-3 bg-black/40 border border-white/10 rounded">
            <div className="text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Shield className="w-3 h-3" /> Distribution
            </div>
            <div className="font-mono text-zinc-300 text-[10px]">{distribution}</div>
          </div>
        </div>

        {/* Threat & Confidence Indicators */}
        {(threatLevel || confidence) && (
          <div className="flex flex-wrap gap-3 mt-4">
            {threatLevel && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded border ${threatStyle.bg} ${threatStyle.border}`}>
                <ThreatIcon className={`w-4 h-4 ${threatStyle.text}`} />
                <span className={`text-xs font-bold ${threatStyle.text}`}>THREAT LEVEL: {threatLevel}</span>
              </div>
            )}
            {confidence && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded border ${
                confidence >= 0.8 ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' :
                confidence >= 0.5 ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400' :
                'bg-red-500/20 border-red-500/50 text-red-400'
              }`}>
                <Activity className="w-4 h-4" />
                <span className="text-xs font-bold">CONFIDENCE: {(confidence * 100).toFixed(0)}%</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Executive Summary (BLUF) Component
export function ExecutiveSummary({ content, highlights = [] }) {
  return (
    <div className="relative">
      {/* BLUF Label */}
      <div className="absolute -top-3 left-4 px-3 py-1 bg-[#00FF9D] text-black text-[10px] font-bold tracking-wider z-10">
        BLUF - BOTTOM LINE UP FRONT
      </div>
      
      <div className="p-6 bg-gradient-to-r from-[#00FF9D]/10 to-transparent border-l-4 border-[#00FF9D] rounded-r-lg">
        <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
          {content || 'Executive summary pending analysis...'}
        </div>
        
        {highlights.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#00FF9D]/20">
            <div className="text-[10px] text-[#00FF9D] uppercase tracking-wider mb-2 flex items-center gap-1">
              <Zap className="w-3 h-3" /> Key Highlights
            </div>
            <div className="flex flex-wrap gap-2">
              {highlights.map((h, i) => (
                <span key={i} className="px-2 py-1 text-xs bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30 rounded">
                  {h}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Collapsible Report Section
export function ReportSection({ 
  title, 
  content, 
  icon: Icon = FileText,
  expanded = false,
  onToggle,
  color = 'cyan',
  badge,
  isList = false,
  children
}) {
  const colorClasses = {
    cyan: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', hover: 'hover:border-cyan-500/50' },
    orange: { border: 'border-orange-500/30', bg: 'bg-orange-500/10', text: 'text-orange-400', hover: 'hover:border-orange-500/50' },
    emerald: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', hover: 'hover:border-emerald-500/50' },
    purple: { border: 'border-purple-500/30', bg: 'bg-purple-500/10', text: 'text-purple-400', hover: 'hover:border-purple-500/50' },
    red: { border: 'border-red-500/30', bg: 'bg-red-500/10', text: 'text-red-400', hover: 'hover:border-red-500/50' },
    yellow: { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', text: 'text-yellow-400', hover: 'hover:border-yellow-500/50' },
    blue: { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', hover: 'hover:border-blue-500/50' }
  };

  const c = colorClasses[color] || colorClasses.cyan;

  return (
    <div className={`rounded-lg border ${c.border} ${c.hover} transition-all overflow-hidden`}>
      <button 
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-4 ${c.bg} hover:bg-opacity-50 transition-all`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${c.text}`} />
          <span className={`text-sm font-bold uppercase tracking-wider ${c.text}`} style={{ fontFamily: 'Barlow Condensed' }}>
            {title}
          </span>
          {badge && (
            <span className={`px-2 py-0.5 text-[10px] font-bold ${c.bg} ${c.text} border ${c.border} rounded`}>
              {badge}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className={`w-5 h-5 ${c.text}`} /> : <ChevronDown className={`w-5 h-5 ${c.text}`} />}
      </button>
      
      {expanded && (
        <div className="p-4 border-t border-white/5 bg-black/20">
          {children ? children : (
            isList && Array.isArray(content) ? (
              <ul className="space-y-2">
                {content.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-zinc-300">
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${c.bg} border ${c.border}`}></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {content || 'Analysis in progress...'}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Threat Assessment Panel
export function ThreatAssessmentPanel({ assessment, level, factors = [] }) {
  const levels = {
    'CRITICAL': { color: 'red', width: '100%', desc: 'Immediate action required' },
    'HIGH': { color: 'orange', width: '80%', desc: 'Significant threat detected' },
    'MODERATE': { color: 'yellow', width: '60%', desc: 'Elevated monitoring advised' },
    'LOW': { color: 'emerald', width: '40%', desc: 'Standard protocols apply' },
    'MINIMAL': { color: 'zinc', width: '20%', desc: 'Normal operations' }
  };

  const l = levels[level] || levels['MODERATE'];

  return (
    <div className="p-5 bg-gradient-to-br from-black/50 to-zinc-900/30 border border-white/10 rounded-lg">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className={`w-6 h-6 text-${l.color}-400`} />
        <div>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            THREAT ASSESSMENT
          </h3>
          <p className="text-xs text-zinc-500">{l.desc}</p>
        </div>
      </div>

      {/* Threat Level Indicator */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-zinc-500">Threat Level</span>
          <span className={`font-bold text-${l.color}-400`}>{level}</span>
        </div>
        <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r from-${l.color}-600 to-${l.color}-400 transition-all duration-1000`}
            style={{ width: l.width }}
          ></div>
        </div>
      </div>

      {/* Assessment Text */}
      <div className="text-sm text-zinc-300 leading-relaxed mb-4">
        {assessment || 'Threat assessment pending...'}
      </div>

      {/* Threat Factors */}
      {factors.length > 0 && (
        <div className="pt-4 border-t border-white/10">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Contributing Factors</div>
          <div className="flex flex-wrap gap-2">
            {factors.map((f, i) => (
              <span key={i} className={`px-2 py-1 text-[10px] bg-${l.color}-500/10 text-${l.color}-400 border border-${l.color}-500/30 rounded`}>
                {f}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Source Reliability Matrix
export function SourceReliabilityMatrix({ sources = [] }) {
  const reliabilityScale = {
    'A': { label: 'Completely Reliable', color: 'emerald' },
    'B': { label: 'Usually Reliable', color: 'cyan' },
    'C': { label: 'Fairly Reliable', color: 'yellow' },
    'D': { label: 'Not Usually Reliable', color: 'orange' },
    'E': { label: 'Unreliable', color: 'red' },
    'F': { label: 'Cannot Be Judged', color: 'zinc' }
  };

  const accuracyScale = {
    '1': { label: 'Confirmed', color: 'emerald' },
    '2': { label: 'Probably True', color: 'cyan' },
    '3': { label: 'Possibly True', color: 'yellow' },
    '4': { label: 'Doubtfully True', color: 'orange' },
    '5': { label: 'Improbable', color: 'red' },
    '6': { label: 'Cannot Be Judged', color: 'zinc' }
  };

  return (
    <div className="p-4 bg-black/30 border border-white/10 rounded-lg">
      <div className="flex items-center gap-2 mb-4">
        <Layers className="w-5 h-5 text-purple-400" />
        <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider">Source Reliability Matrix</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-2 px-3 text-zinc-500">Source</th>
              <th className="text-center py-2 px-3 text-zinc-500">Type</th>
              <th className="text-center py-2 px-3 text-zinc-500">Reliability</th>
              <th className="text-center py-2 px-3 text-zinc-500">Accuracy</th>
            </tr>
          </thead>
          <tbody>
            {sources.length > 0 ? sources.map((src, i) => {
              const rel = reliabilityScale[src.reliability] || reliabilityScale['F'];
              const acc = accuracyScale[src.accuracy] || accuracyScale['6'];
              return (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 px-3 text-zinc-300 font-mono">{src.name}</td>
                  <td className="py-2 px-3 text-center">
                    <span className="px-2 py-0.5 text-[10px] bg-zinc-700/50 text-zinc-400 rounded">{src.type}</span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 text-[10px] bg-${rel.color}-500/20 text-${rel.color}-400 rounded font-mono`}>
                      {src.reliability} - {rel.label}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 text-[10px] bg-${acc.color}-500/20 text-${acc.color}-400 rounded font-mono`}>
                      {src.accuracy} - {acc.label}
                    </span>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="4" className="py-4 text-center text-zinc-500">No sources listed</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Intelligence Summary Cards
export function IntelSummaryCards({ data }) {
  const cards = [
    { key: 'objects', icon: Target, label: 'Objects Detected', color: 'cyan' },
    { key: 'threats', icon: AlertTriangle, label: 'Threat Indicators', color: 'red' },
    { key: 'activities', icon: Activity, label: 'Activity Patterns', color: 'orange' },
    { key: 'sources', icon: Layers, label: 'Data Sources', color: 'purple' }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map(card => (
        <div key={card.key} className={`p-4 bg-gradient-to-br from-${card.color}-500/10 to-transparent border border-${card.color}-500/20 rounded-lg text-center`}>
          <card.icon className={`w-6 h-6 text-${card.color}-400 mx-auto mb-2`} />
          <div className={`text-2xl font-bold text-${card.color}-400`}>{data?.[card.key] || 0}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{card.label}</div>
        </div>
      ))}
    </div>
  );
}

// Recommendations Panel
export function RecommendationsPanel({ recommendations = [], priority = 'IMMEDIATE' }) {
  const priorityStyles = {
    'IMMEDIATE': { color: 'red', icon: Zap },
    'HIGH': { color: 'orange', icon: AlertTriangle },
    'STANDARD': { color: 'cyan', icon: Info },
    'ADVISORY': { color: 'zinc', icon: Info }
  };

  const p = priorityStyles[priority] || priorityStyles['STANDARD'];
  const PriorityIcon = p.icon;

  return (
    <div className={`p-5 bg-gradient-to-br from-${p.color}-500/10 to-transparent border border-${p.color}-500/30 rounded-lg`}>
      <div className="flex items-center gap-3 mb-4">
        <PriorityIcon className={`w-5 h-5 text-${p.color}-400`} />
        <div>
          <h3 className={`text-sm font-bold text-${p.color}-400 uppercase tracking-wider`}>
            ANALYST RECOMMENDATIONS
          </h3>
          <span className={`text-[10px] text-${p.color}-400/70`}>Priority: {priority}</span>
        </div>
      </div>

      <ol className="space-y-3">
        {recommendations.length > 0 ? recommendations.map((rec, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-bold bg-${p.color}-500/20 text-${p.color}-400 border border-${p.color}-500/30 rounded-full`}>
              {i + 1}
            </span>
            <span className="text-sm text-zinc-300 leading-relaxed">{rec}</span>
          </li>
        )) : (
          <li className="text-sm text-zinc-500">No specific recommendations at this time.</li>
        )}
      </ol>
    </div>
  );
}

// Location Context Panel
export function LocationContextPanel({ location, context }) {
  return (
    <div className="p-4 bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border border-indigo-500/30 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-indigo-400" />
        <span className="text-sm font-bold text-indigo-400 uppercase tracking-wider">Location Analysis</span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-xs">
        <div>
          <span className="text-zinc-500">Coordinates:</span>
          <p className="text-zinc-300 font-mono mt-1">
            {location?.lat?.toFixed(4)}°N, {location?.lon?.toFixed(4)}°E
          </p>
        </div>
        <div>
          <span className="text-zinc-500">Region:</span>
          <p className="text-zinc-300 mt-1">{location?.region || 'Unknown'}</p>
        </div>
        {context?.strategic_importance && (
          <div className="col-span-2">
            <span className="text-zinc-500">Strategic Importance:</span>
            <p className="text-zinc-300 mt-1">{context.strategic_importance}</p>
          </div>
        )}
        {context?.coordinates?.terrain_type && (
          <div className="col-span-2">
            <span className="text-zinc-500">Terrain:</span>
            <p className="text-zinc-300 mt-1">{context.coordinates.terrain_type}</p>
          </div>
        )}
        {context?.monitoring_priority && (
          <div>
            <span className="text-zinc-500">Monitoring Priority:</span>
            <span className={`ml-2 px-2 py-0.5 text-[10px] rounded ${
              context.monitoring_priority === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
              context.monitoring_priority === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>{context.monitoring_priority}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Report Footer
export function ReportFooter({ classification = 'UNCLASSIFIED', reportId, generatedAt }) {
  return (
    <div className="mt-6 pt-4 border-t border-white/10">
      <div className="flex flex-wrap items-center justify-between gap-4 text-[10px] text-zinc-500">
        <div className="flex items-center gap-4">
          <span className="font-mono">REPORT ID: {reportId?.slice(0, 12) || 'N/A'}</span>
          <span className="font-mono">GENERATED: {generatedAt || new Date().toISOString()}</span>
        </div>
        <div className="flex items-center gap-2">
          <Lock className="w-3 h-3" />
          <span>{classification} // DEFENSEWATCH PLATFORM</span>
        </div>
      </div>
      <div className="h-1 mt-4 bg-gradient-to-r from-transparent via-[#00FF9D]/50 to-transparent"></div>
    </div>
  );
}

// Detected Object Card (for IMINT reports)
export function DetectedObjectCard({ object, index, expanded, onToggle }) {
  const getCategoryStyle = (cat) => {
    const styles = {
      'GROUND': { icon: '🚛', color: 'emerald', gradient: 'from-emerald-600/20 to-emerald-500/10' },
      'AIR': { icon: '✈️', color: 'cyan', gradient: 'from-cyan-600/20 to-cyan-500/10' },
      'NAVAL': { icon: '🚢', color: 'blue', gradient: 'from-blue-600/20 to-blue-500/10' },
      'INFRASTRUCTURE': { icon: '🏗️', color: 'purple', gradient: 'from-purple-600/20 to-purple-500/10' }
    };
    return styles[cat] || { icon: '📍', color: 'zinc', gradient: 'from-zinc-600/20 to-zinc-500/10' };
  };

  const getCountryFlag = (country) => {
    const flags = {
      'Russia': '🇷🇺', 'USA': '🇺🇸', 'China': '🇨🇳', 'Israel': '🇮🇱', 'Germany': '🇩🇪',
      'UK': '🇬🇧', 'France': '🇫🇷', 'Turkey': '🇹🇷', 'Iran': '🇮🇷', 'South Korea': '🇰🇷'
    };
    return flags[country] || '🏳️';
  };

  const catStyle = getCategoryStyle(object.category);

  return (
    <div 
      className={`p-4 bg-gradient-to-r ${catStyle.gradient} border border-${catStyle.color}-500/20 rounded-lg hover:border-${catStyle.color}-500/40 transition-all cursor-pointer`}
      onClick={() => onToggle(index)}
    >
      {/* Object Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="text-2xl p-2 bg-black/30 rounded-lg">{catStyle.icon}</div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">{object.object_type}</span>
              {object.origin_country && (
                <span className="text-sm" title={object.origin_country}>{getCountryFlag(object.origin_country)}</span>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              {object.category} • Qty: <span className="text-white font-semibold">{object.quantity || 1}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className={`px-2 py-1 text-[10px] font-bold border rounded ${
            object.confidence === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' :
            object.confidence === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' :
            'bg-red-500/20 text-red-400 border-red-500/50'
          }`}>
            {object.confidence} {object.confidence_score ? `• ${(object.confidence_score * 100).toFixed(0)}%` : ''}
          </span>
          {object.threat_level && (
            <span className={`px-2 py-1 text-[10px] font-bold border rounded ${
              object.threat_level === 'CRITICAL' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
              object.threat_level === 'HIGH' ? 'bg-orange-500/20 text-orange-400 border-orange-500/50' :
              'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
            }`}>
              {object.threat_level} THREAT
            </span>
          )}
        </div>
      </div>

      {/* Reference Image */}
      {object.reference_image && (
        <div className="mb-3 relative group">
          <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-[9px] text-cyan-400 rounded z-10">
            REFERENCE IMAGE
          </div>
          <img 
            src={object.reference_image} 
            alt={`${object.object_type} reference`}
            className="w-full h-32 object-cover rounded-lg border border-white/10 group-hover:border-cyan-500/30 transition-all"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      {/* Object Details */}
      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div className="p-2 bg-black/20 rounded">
          <span className="text-zinc-500 text-[10px] uppercase">Location</span>
          <div className="text-zinc-300 font-mono text-[11px] mt-0.5">{object.coordinates}</div>
        </div>
        {object.reference_match && (
          <div className="p-2 bg-black/20 rounded">
            <span className="text-zinc-500 text-[10px] uppercase">Database Match</span>
            <div className="text-cyan-400 text-[11px] mt-0.5">{object.reference_match}</div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-3 animate-in slide-in-from-top-2">
          {/* Visual Indicators */}
          {object.indicators && object.indicators.length > 0 && (
            <div>
              <div className="text-[10px] text-zinc-500 uppercase mb-2 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Visual Identification Indicators
              </div>
              <div className="flex flex-wrap gap-1.5">
                {object.indicators.map((ind, i) => (
                  <span key={i} className="px-2 py-1 text-[10px] bg-white/5 text-zinc-300 border border-white/10 rounded-full">
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Location Analysis */}
          {object.location_analysis && (
            <div className="p-3 bg-black/30 rounded-lg">
              <div className="text-[10px] text-indigo-400 uppercase mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Location Context
              </div>
              <div className="text-xs text-zinc-400">
                <p>{object.terrain_context || object.location_analysis.terrain_type}</p>
                {object.location_analysis.strategic_importance && (
                  <p className="mt-1 text-zinc-300">{object.location_analysis.strategic_importance}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Description */}
          {object.description && (
            <p className="text-xs text-zinc-400 italic p-2 bg-black/20 rounded">{object.description}</p>
          )}
        </div>
      )}

      {/* Expand hint */}
      <div className="text-center mt-2">
        <span className="text-[9px] text-zinc-600">
          {expanded ? '▲ Click to collapse' : '▼ Click for details'}
        </span>
      </div>
    </div>
  );
}

// Export Actions Bar
export function ExportActionsBar({ onExportPDF, onExportPPTX, onExportJSON, onPrint, onShare, exporting }) {
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-black/30 border border-white/10 rounded-lg">
      <span className="text-xs text-zinc-500 uppercase tracking-wider mr-2">Export Report:</span>
      
      {onExportPPTX && (
        <button
          onClick={onExportPPTX}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded hover:bg-orange-500/30 transition-all disabled:opacity-50 text-xs"
        >
          <Download className="w-4 h-4" />
          PowerPoint
        </button>
      )}
      
      {onExportPDF && (
        <button
          onClick={onExportPDF}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded hover:bg-red-500/30 transition-all disabled:opacity-50 text-xs"
        >
          <Download className="w-4 h-4" />
          PDF
        </button>
      )}
      
      {onExportJSON && (
        <button
          onClick={onExportJSON}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 rounded hover:bg-cyan-500/30 transition-all disabled:opacity-50 text-xs"
        >
          <Download className="w-4 h-4" />
          JSON
        </button>
      )}
      
      {onPrint && (
        <button
          onClick={onPrint}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-500/20 text-zinc-400 border border-zinc-500/50 rounded hover:bg-zinc-500/30 transition-all text-xs"
        >
          <FileText className="w-4 h-4" />
          Print
        </button>
      )}
    </div>
  );
}

// Main Report Container
export function IntelligenceReport({ 
  classification = 'UNCLASSIFIED',
  tlp = 'CLEAR',
  title,
  subtitle,
  reportId,
  date,
  threatLevel,
  confidence,
  children 
}) {
  return (
    <div className="bg-gradient-to-br from-zinc-900/95 to-black/95 rounded-lg overflow-hidden shadow-2xl border border-white/10" data-testid="intelligence-report">
      <ClassificationBanner level={classification} tlp={tlp} />
      <ReportHeader 
        title={title}
        subtitle={subtitle}
        reportId={reportId}
        date={date}
        threatLevel={threatLevel}
        confidence={confidence}
      />
      <div className="p-6 space-y-6">
        {children}
      </div>
      <div className="px-6">
        <ReportFooter classification={classification} reportId={reportId} generatedAt={date} />
      </div>
      <ClassificationBanner level={classification} tlp={tlp} />
    </div>
  );
}
